import { useLocation, Navigate, Outlet } from "react-router-dom";
import jwt_decode from "jwt-decode";
import { useAuth } from "./AuthProvider.js";
import { buildLoginRedirectFrom } from "./redirect.js";

// One [resource, action] pair or an array of pairs (any-of) -> an array of pairs.
const toPairs = (permission) => {
  if (!Array.isArray(permission) || permission.length === 0) return [];
  return typeof permission[0] === "string" ? [permission] : permission;
};

// Route guard:
//   - /login without a session;
//   - /unauthorized when the account type (token role) is not in allowedRoles. No bypass: the platform
//     ADMIN passes only the groups that list ROLES.ADMIN. An empty allowedRoles lets any account in;
//   - /unauthorized when `permission` is given and canAccess(resource, action) is false for every pair.
//     `permission` is [resource, action] or an array of such pairs (any-of). The check is injected
//     (`canAccess`), the package knows no store; with a permission and no canAccess nothing passes.
// `ready` false (the permissions are still loading) renders `fallback` and never redirects: it only
// matters once the account type has passed, so a wrong account is not held on a loader.
const RequireAuth = ({ allowedRoles = [], permission, canAccess, ready = true, fallback = null }) => {
  const { auth } = useAuth();
  const location = useLocation();

  if (!auth?.accessToken) {
    const from = buildLoginRedirectFrom(`${location.pathname}${location.search}`);
    return <Navigate to="/login" state={{ from }} replace />;
  }

  const role = Number(jwt_decode(auth.accessToken)?.UserInfo?.role);
  const hasRole = allowedRoles.length === 0 || allowedRoles.some((r) => Number(r) === role);
  if (!hasRole) {
    return <Navigate to="/unauthorized" state={{ from: location }} replace />;
  }

  const pairs = toPairs(permission);
  if (pairs.length > 0) {
    if (!ready) return fallback;
    const allowed = typeof canAccess === "function" && pairs.some(([resource, action]) => canAccess(resource, action));
    if (!allowed) {
      return <Navigate to="/unauthorized" state={{ from: location }} replace />;
    }
  }

  return <Outlet />;
};

export default RequireAuth;
